import { useState, useEffect, useRef, useCallback } from 'react';
import { data_from_server_post } from '@/shared/http/client';
import { DatasetOrigin } from '@/shared/domain/taskTypes';
import { sparkToast } from '@spark';
import { formatFiltersForAPI } from '@/features/datasets/utils/formatFiltersForAPI';

export const useDataFetching = (currentUsecase, isDataSelectionScreen = false, currentFilters, activeSorter, defaultPageSize = 10) => {
    const [state, setState] = useState({
        dataset: [],
        benchmark: [],
        datasetMetadata: {},
        benchmarkMetadata: {},
        availableExtraParamsKeys: {
            dataset: [],
            benchmark: [],
            manual_model_predictions: [],
        },
        crossReferenceFields: {},
        processedTrainsetInfo: {},
        isDatasetLoading: true,
        isBenchmarkLoading: true,
        isDatasetAndBenchmarkMetadataLoading: true,
        datasetFilteredCount: null,
        datasetFilteredLabels: [],
        benchmarkFilteredCount: null,
        benchmarkFilteredLabels: [],
        hasDatasetFilters: false,
        hasBenchmarkFilters: false,
    });

    // Track whether polling is active
    const isDatasetPollingActive = useRef(true);
    const isBenchmarkPollingActive = useRef(true);
    const isMetadataPollingActive = useRef(true);

    // Timeout refs
    const datasetTimeoutRef = useRef(null);
    const benchmarkTimeoutRef = useRef(null);
    const metadataTimeoutRef = useRef(null);

    const METADATA_POLL_INTERVAL_MS = 5000;

    // Train/benchmark table polling only applies on the data-selection screen.
    // Metadata may still poll on other routes so the task overview can reflect
    // ingestion progress — do not clear metadata timers here.
    useEffect(() => {
        if (!isDataSelectionScreen) {
            isDatasetPollingActive.current = false;
            isBenchmarkPollingActive.current = false;
            if (datasetTimeoutRef.current) {
                clearTimeout(datasetTimeoutRef.current);
                datasetTimeoutRef.current = null;
            }
            if (benchmarkTimeoutRef.current) {
                clearTimeout(benchmarkTimeoutRef.current);
                benchmarkTimeoutRef.current = null;
            }
        } else {
            isDatasetPollingActive.current = true;
            isBenchmarkPollingActive.current = true;
        }
    }, [isDataSelectionScreen]);

    const shouldPollMetadata = (result) => {
        try {
            const dataset = result?.dataset || {};
            const benchmark = result?.benchmark || {};

            // If we know the file-level total count and the processed record count hasn't reached it yet,
            // keep polling to reflect progress while ingestion/processing is still running.
            const datasetTotal = Number(dataset?.total_num_records || 0);
            const datasetProcessed = Number(
                dataset?.processed_num_records ?? dataset?.num_records ?? 0
            );
            if (datasetTotal > 0 && datasetProcessed < datasetTotal) return true;

            const benchmarkTotal = Number(benchmark?.total_num_records || 0);
            const benchmarkProcessed = Number(
                benchmark?.processed_num_records ?? benchmark?.num_records ?? 0
            );
            if (benchmarkTotal > 0 && benchmarkProcessed < benchmarkTotal) return true;

            // If any dataset/benchmark in datasets_info still doesn't have summary data, keep polling.
            if (Array.isArray(result?.datasets_info)) {
                const hasIncomplete = result.datasets_info.some((d) => {
                    const totalInFile = Number(d?.total_records_in_file || 0);
                    const hasSummary = Boolean(d?.request_example_summary_data);
                    return totalInFile > 0 && !hasSummary;
                });
                if (hasIncomplete) return true;
            }
        } catch (e) {
            // In doubt, do NOT poll aggressively.
            return false;
        }
        return false;
    };

    const fetchMetadata = async (llm_usecase_id, client_id) => {
        if (!isMetadataPollingActive.current) return;

        data_from_server_post(
            "datasets-and-benchmarks-meta-data",
            { client_id, llm_usecase_id },
            (result) => {
                setState(prev => ({
                    ...prev,
                    datasetMetadata: result.dataset,
                    benchmarkMetadata: result.benchmark,
                    availableExtraParamsKeys: result.available_extra_params_keys || prev.availableExtraParamsKeys,
                    crossReferenceFields: result.cross_reference_fields || prev.crossReferenceFields,
                    processedTrainsetInfo: {
                        key: 1,
                        num_rows_entries: result.dataset.num_records,
                        num_greens: result.dataset.num_high_quality,
                        num_oranges: result.dataset.num_medium_quality,
                        num_reds: result.dataset.num_low_quality,
                    },
                    isDatasetAndBenchmarkMetadataLoading: false
                }));

                // Poll while ingestion/summary work is incomplete (any route).
                if (
                    isMetadataPollingActive.current &&
                    currentUsecase &&
                    shouldPollMetadata(result)
                ) {
                    metadataTimeoutRef.current = setTimeout(() => {
                        fetchMetadata(llm_usecase_id, client_id);
                    }, METADATA_POLL_INTERVAL_MS);
                } else {
                    isMetadataPollingActive.current = false;
                }
            }
        );
    };

    const updateRecord = useCallback((record, is_benchmark) => {
        setState(prev => {
            const datasetKey = is_benchmark ? 'benchmark' : 'dataset';
            const recordIndex = prev[datasetKey].findIndex(item => item._id === record._id);

            if (recordIndex !== -1) {
                return {
                    ...prev,
                    [datasetKey]: [
                        ...prev[datasetKey].slice(0, recordIndex),
                        record,
                        ...prev[datasetKey].slice(recordIndex + 1)
                    ]
                };
            } else {
                console.log('Failed to find record in dataset with id:', record._id);
                return prev;
            }
        });
    }, []);

    const getDatasetData = async (first_index, num_records, llm_usecase_id, client_id, is_benchmark, filters = {}, sorter = null, fromFiltersChange = false, callback = null) => {
        // Check if polling should be active for this type of data
        if (((is_benchmark && !isBenchmarkPollingActive.current) ||
            (!is_benchmark && !isDatasetPollingActive.current)) && !fromFiltersChange) {
            return;
        }

        setState(prev => ({
            ...prev,
            [is_benchmark ? 'isBenchmarkLoading' : 'isDatasetLoading']: true,
        }));

        const formattedFilters = formatFiltersForAPI(filters);

        let sortField = null;
        let sortOrder = null;

        if (sorter && sorter.field && sorter.order) {
            sortField = sorter.field;
            sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
        }

        const hasFiltersOrSorters = Object.keys(formattedFilters).length > 0 || sortField;
        data_from_server_post(
            "dataset-records",
            {
                client_id,
                llm_usecase_id,
                first_index,
                num_records,
                is_benchmark,
                also_null_request_examples: false,
                filters: Object.keys(formattedFilters).length > 0 ? formattedFilters : undefined,
                sort_field: sortField,
                sort_order: sortOrder,
                also_null_responses: true,
            },
            (results) => {
                let records = results?.records;
                const filtered_labels = results?.filtered_labels;
                // Safety check - if result is not an array, convert to empty array to prevent table disappearing
                if (!Array.isArray(records)) {
                    console.error('Expected array result but got:', records);
                    records = [];
                }
                setState(prev => {
                    const newState = {
                        ...prev,
                        [is_benchmark ? 'benchmark' : 'dataset']: records,
                        [is_benchmark ? 'isBenchmarkLoading' : 'isDatasetLoading']: false,
                    };
                    if (hasFiltersOrSorters && records.length === 0) {
                        console.warn(`Filter returned no results. Showing empty state but keeping table visible.`);
                        newState[is_benchmark ? 'benchmark' : 'dataset'] = [];
                    }

                    if (hasFiltersOrSorters) {
                        newState[is_benchmark ? 'hasBenchmarkFilters' : 'hasDatasetFilters'] = true;
                    } else {
                        newState[is_benchmark ? 'hasBenchmarkFilters' : 'hasDatasetFilters'] = false;
                    }
                    newState[is_benchmark ? 'benchmarkFilteredCount' : 'datasetFilteredCount'] = results?.filtered_count;
                    newState[is_benchmark ? 'benchmarkFilteredLabels' : 'datasetFilteredLabels'] = filtered_labels;
                    return newState;
                });
                if (callback) {
                    callback(records);
                }
                // Only schedule next poll if still on data selection screen
                if (hasFiltersOrSorters) {
                    if (is_benchmark) {
                        isBenchmarkPollingActive.current = false;
                    } else {
                        isDatasetPollingActive.current = false;
                    }
                } else {
                    // Schedule next fetch if polling is still active
                    if ((is_benchmark && isBenchmarkPollingActive.current) ||
                        (!is_benchmark && isDatasetPollingActive.current)) {

                        // Keep polling if any record is waiting for AI label generation.
                        const hasRecordsAwaitingLabels = records && records.some(r =>
                            r.data_creation_type === DatasetOrigin.specific_ai_labeled &&
                            (!r.labels || r.labels.length === 0 || (r.labels.length === 1 && !r.labels[0]))
                        );

                        if (records && records.length >= num_records && !hasRecordsAwaitingLabels) {
                            console.log(`${is_benchmark ? 'Benchmark' : 'Dataset'} table is full. Stopping polling.`);
                            if (is_benchmark) {
                                isBenchmarkPollingActive.current = false;
                            } else {
                                isDatasetPollingActive.current = false;
                            }
                        } else if (isDataSelectionScreen) {
                            // Only schedule next poll if still on data selection screen
                            if (is_benchmark) {
                                benchmarkTimeoutRef.current = setTimeout(() => {
                                    if (currentUsecase) {
                                        getDatasetData(0, num_records, llm_usecase_id, client_id, true, filters, activeSorter);
                                    }
                                }, 5000);
                            } else {
                                datasetTimeoutRef.current = setTimeout(() => {
                                    if (currentUsecase) {
                                        getDatasetData(0, num_records, llm_usecase_id, client_id, false, filters, activeSorter);
                                    }
                                }, 5000);
                            }
                        }
                    }
                }
            },
            () => {
                // A failed records request must leave the table settled.
                // An empty page then reads "No data" instead of a skeleton
                // that never comes down.
                setState(prev => ({
                    ...prev,
                    [is_benchmark ? 'isBenchmarkLoading' : 'isDatasetLoading']: false,
                }));
            }
        );
    };

    useEffect(() => {
        if (!currentUsecase?._id || !currentUsecase?.client_id) return;

        const { client_id, _id: llm_usecase_id } = currentUsecase;

        // Do not reset dataset polling true on routes that only need sidebar metadata (see screen effect).
        if (isDataSelectionScreen) {
            isDatasetPollingActive.current = true;
            isBenchmarkPollingActive.current = true;
        } else {
            isDatasetPollingActive.current = false;
            isBenchmarkPollingActive.current = false;
        }
        isMetadataPollingActive.current = true;

        if (datasetTimeoutRef.current) clearTimeout(datasetTimeoutRef.current);
        if (benchmarkTimeoutRef.current) clearTimeout(benchmarkTimeoutRef.current);
        if (metadataTimeoutRef.current) clearTimeout(metadataTimeoutRef.current);

        // Records table + dataset-records polling: data-selection page only.
        if (isDataSelectionScreen) {
            getDatasetData(0, defaultPageSize, llm_usecase_id, client_id, false, currentFilters, activeSorter);
            getDatasetData(0, defaultPageSize, llm_usecase_id, client_id, true, currentFilters, activeSorter);
        }
        fetchMetadata(llm_usecase_id, client_id);

        return () => {
            isDatasetPollingActive.current = false;
            isBenchmarkPollingActive.current = false;
            isMetadataPollingActive.current = false;

            if (datasetTimeoutRef.current) clearTimeout(datasetTimeoutRef.current);
            if (benchmarkTimeoutRef.current) clearTimeout(benchmarkTimeoutRef.current);
            if (metadataTimeoutRef.current) clearTimeout(metadataTimeoutRef.current);
        };
    }, [currentUsecase?._id, currentUsecase?.client_id, isDataSelectionScreen, defaultPageSize]);

    const handleTableChange = (current_page, pageSize, is_benchmark, filters = {}, sorter = null, callback = null) => {
        if (!currentUsecase) return;

        const { client_id, _id: llm_usecase_id } = currentUsecase;
        const first_index = (current_page - 1) * pageSize;

        // Clear existing timeouts
        if (is_benchmark) {
            if (benchmarkTimeoutRef.current) {
                clearTimeout(benchmarkTimeoutRef.current);
                benchmarkTimeoutRef.current = null;
            }
        } else {
            if (datasetTimeoutRef.current) {
                clearTimeout(datasetTimeoutRef.current);
                datasetTimeoutRef.current = null;
            }
        }

        // Handle filters/sorters
        // Check for array filters (length > 0) or boolean filters (is_in_dataset)
        const hasFiltersOrSorters =
            (filters && Object.keys(filters).some(key => {
                const value = filters[key];
                // Handle boolean filters (like is_in_dataset)
                if (typeof value === 'boolean') return true;
                // Handle array filters
                return value?.length > 0;
            })) ||
            (sorter && sorter.field && sorter.order);

        // Set polling state based on filters
        if (is_benchmark) {
            isBenchmarkPollingActive.current = !hasFiltersOrSorters;
        } else {
            isDatasetPollingActive.current = !hasFiltersOrSorters;
        }

        // Use the provided sorter only. Do not force a default here to avoid unintended empty results
        // and to keep polling behavior consistent (only stop polling when the user actually sorts/filters).
        const effectiveSorter = sorter;

        // Fetch data
        getDatasetData(first_index, pageSize, llm_usecase_id, client_id, is_benchmark, filters, effectiveSorter, true, callback);
    };

    const setIsInDB = (id, value, dataset, isBenchmark, currentPage, pageSize) => {
        if (!currentUsecase) return;

        data_from_server_post(
            "set-is-in-db",
            {
                id,
                is_in_dataset: value,
                client_id: currentUsecase.client_id,
                is_benchmark: isBenchmark,
                dataset: dataset,
            },
            () => {
                handleTableChange(currentPage, pageSize, isBenchmark, currentFilters, activeSorter);
            },
            () => {
                sparkToast.error('Failed to update dataset');
            }
        );
    };

    const getMetadata = () => {
        return {
            datasetMetadata: {
                ...state.datasetMetadata,
                filteredRecords: state.hasDatasetFilters && state.datasetFilteredCount !== null
                    ? state.datasetFilteredCount
                    : state.datasetMetadata.num_records,
                filteredLabels: state.datasetFilteredLabels
            },
            benchmarkMetadata: {
                ...state.benchmarkMetadata,
                filteredRecords: state.hasBenchmarkFilters && state.benchmarkFilteredCount !== null
                    ? state.benchmarkFilteredCount
                    : state.benchmarkMetadata.num_records,
                filteredLabels: state.benchmarkFilteredLabels
            }
        };
    };

    return {
        ...state,
        datasetMetadata: getMetadata().datasetMetadata,
        benchmarkMetadata: getMetadata().benchmarkMetadata,
        handleTableChange,
        setIsInDB,
        updateRecord,
    };
};